import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { dailyControlRequest } from './dailyControlApi'
import { MUI_TABLE_PAGINATION_OPTIONS } from '../../components/MuiTablePagination'

import { DEFAULT_FILTERS, STATUS_OPTIONS } from './constants/dailySettlementControlConstants'

export default function useDailySettlementControl() {
  const { pathname, search } = useLocation()
  const navigate = useNavigate()
  // La URL es la fuente de los filtros, también al recargar o volver con el navegador.
  const params = useMemo(() => {
    const query = new URLSearchParams(search)
    const page = Number(query.get('page'))
    const perPage = Number(query.get('per_page'))
    const status = query.get('status')
    return {
      date: query.get('date') || null,
      q: query.get('q') || '',
      status: STATUS_OPTIONS.some((option) => option.value === status)
        ? status
        : DEFAULT_FILTERS.status,
      sort_by: query.get('sort_by') || null,
      sort_direction: query.get('sort_direction') === 'asc' ? 'asc' : 'desc',
      page: Number.isInteger(page) && page > 0 ? page : DEFAULT_FILTERS.page,
      per_page: MUI_TABLE_PAGINATION_OPTIONS.includes(perPage) ? perPage : DEFAULT_FILTERS.per_page
    }
  }, [search])
  const [searchInput, setSearchInput] = useState(params.q)
  const [data, setData] = useState(null)
  const [chargesCache, setChargesCache] = useState(() => new Map())
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [expandedUid, setExpandedUid] = useState(null)

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setError('')
    dailyControlRequest(params, { signal: controller.signal })
      .then((result) => {
        if (controller.signal.aborted) return
        setData(result)
        // Cada nueva carga del listado descarta los detalles de la consulta anterior.
        setChargesCache(new Map())
        setHasLoaded(true)
        setExpandedUid((uid) => (result.result.some((row) => row.user_uid === uid) ? uid : null))
      })
      .catch((reason) => {
        if (!controller.signal.aborted) {
          setError(reason.message)
          setData(null)
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [params])

  useEffect(() => {
    setSearchInput(params.q)
  }, [search, params.q])

  useEffect(() => setExpandedUid(null), [params.date])

  const update = (changes) => {
    const next = {
      ...params,
      date: params.date || data?.date || null,
      page: 1,
      ...changes
    }
    const query = new URLSearchParams()
    Object.entries(next).forEach(([key, value]) => {
      if (value != null && value !== '' && value !== DEFAULT_FILTERS[key]) {
        query.set(key, value)
      }
    })
    const nextSearch = query.toString()
    if (nextSearch === search.replace(/^\?/, '')) return
    setLoading(true)
    navigate({ pathname, search: nextSearch ? `?${nextSearch}` : '' })
  }
  const submitSearch = (value) => {
    update({ q: value.trim() })
  }
  const changeSearch = (value) => {
    setSearchInput(value)
  }
  const changeDate = (date) => {
    setExpandedUid(null)
    update({ date })
  }
  const sort = (column) => {
    if (params.sort_by !== column) update({ sort_by: column, sort_direction: 'desc' })
    else if (params.sort_direction === 'desc') update({ sort_direction: 'asc' })
    else update({ sort_by: null, sort_direction: 'desc' })
  }
  const reset = () => {
    setSearchInput('')
    update({ q: '', status: 'all', sort_by: null, sort_direction: 'desc' })
  }

  return {
    params,
    searchInput,
    data,
    chargesCache,
    error,
    loading,
    hasLoaded,
    expandedUid,
    setExpandedUid,
    changeSearch,
    submitSearch,
    changeDate,
    sort,
    reset,
    update
  }
}
